import React, { useMemo } from "react";

import Form, { FormProps } from "antd/es/form";
import Radio from "antd/es/radio";
import Select, { LabeledValue } from "antd/es/select";
import addMinutes from "date-fns/fp/addMinutes";
import isBefore from "date-fns/fp/isBefore";
import isSameDay from "date-fns/fp/isSameDay";
import isSameHour from "date-fns/fp/isSameHour";
import startOfDay from "date-fns/fp/startOfDay";
import range from "lodash-es/range";
import compose from "lodash/fp/compose";
import { useIntl } from "react-intl";

import FrequencyType from "@mapmycustomers/shared/enum/FrequencyType";

import DatePicker from "@app/component/input/DatePicker";
import TimeZoneSelect from "@app/component/TimeZoneSelect";
import Report from "@app/types/Report";
import TimeZone from "@app/types/TimeZone";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import getFrequencyTypeDisplayName from "@app/util/getFrequencyTypeDisplayName";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import { EmailSelect } from "../EmailSelect";

import styles from "./EditScheduleForm.module.scss";
import messages from "./messages";

type FrequencyOption = "" | FrequencyType;

export type FormState = {
  dateTime: Date | null;
  frequency: FrequencyOption;
  recipients: Report["sendOptions"]["to"];
  sendFullReport: Report["sendOptions"]["sendFullReport"];
  timezone: TimeZone;
};

type PickerProps = React.ComponentProps<typeof DatePicker>;

export const CRON_MIN_STEP = 10;

const getMinDate = () => addMinutes(CRON_MIN_STEP, new Date());

const createBeforeMinDayPredicate = compose<[], Date, Date, (d: Date) => boolean>(
  isBefore,
  startOfDay,
  getMinDate
);

const disabledDate: PickerProps["disabledDate"] = (current) =>
  createBeforeMinDayPredicate()(current);

const disabledDateTime = (selectedDate: Date | null) => {
  const minDate = getMinDate();
  const current = selectedDate ?? new Date();

  return {
    disabledHours: () => {
      const hours = current && isSameDay(minDate, current) ? minDate.getHours() : undefined;
      return range(0, 23).splice(0, hours);
    },
    disabledMinutes: () => {
      const minutes = current && isSameHour(minDate, current) ? minDate.getMinutes() : undefined;
      return range(0, 59).splice(0, minutes);
    },
  };
};

interface Props extends FormProps {
  emails: string[];
  myEmail: string;
  report?: Report;
  timezone: TimeZone;
}

const EditScheduleForm = ({ emails, form, myEmail, onFinish, report, timezone }: Props) => {
  const intl = useIntl();

  const frequencyOptions = useMemo<LabeledValue[]>(() => {
    const options = Object.entries(FrequencyType).map<LabeledValue>(([name, value]) => ({
      key: name,
      label: getFrequencyTypeDisplayName(intl, value),
      value,
    }));

    options.unshift({
      label: intl.formatMessage(messages.none),
      value: "",
    });

    return options;
  }, [intl]);

  return (
    <Form<FormState>
      form={form}
      initialValues={{
        dateTime: report?.nextRunAt ? parseApiDateWithUtcTz(report.nextRunAt) : null,
        frequency: report?.frequency ? report?.frequency : "",
        recipients: report?.sendOptions?.to ?? [myEmail],
        sendFullReport: report?.sendOptions?.sendFullReport ?? false,
        timezone: report?.timezone ? report?.timezone : timezone,
      }}
      layout="vertical"
      onFinish={onFinish}
    >
      <div className={styles.container}>
        <Form.Item
          label={intl.formatMessage(messages.dateTimeField)}
          name="dateTime"
          rules={[
            {
              message: intl.formatMessage(messages.validationRequired),
              required: true,
            },
          ]}
          trigger="onSelect"
        >
          <DatePicker
            className={styles.fullWidth}
            disabledDate={disabledDate}
            disabledTime={disabledDateTime}
            format={`yyyy-MM-dd ${SHOULD_USE_12_HOUR_FORMAT ? "hh:mm a" : "HH:mm"}`}
            showNow={false}
            showSecond={false}
            showTime
            use12Hours={SHOULD_USE_12_HOUR_FORMAT}
          />
        </Form.Item>
        <Form.Item label={intl.formatMessage(messages.selectFrequency)} name="frequency">
          <Select<FrequencyOption> className={styles.fullWidth} options={frequencyOptions} />
        </Form.Item>
      </div>
      <div>
        <Form.Item label={intl.formatMessage(messages.selectTimezone)} name="timezone">
          <TimeZoneSelect className={styles.fullWidth} value={timezone} />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage(messages.recipients)}
          name="recipients"
          rules={[{ required: true }]}
        >
          <EmailSelect className={styles.fullWidth} initialEmails={emails} />
        </Form.Item>
        <Form.Item label={intl.formatMessage(messages.exportPreferences)} name="sendFullReport">
          <Radio.Group buttonStyle="solid">
            <Radio.Button value={false}>{intl.formatMessage(messages.newOrModified)}</Radio.Button>
            <Radio.Button value={true}>{intl.formatMessage(messages.all)}</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>
    </Form>
  );
};

export default EditScheduleForm;
